import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import { InputGroup, Button, Spinner } from "react-bootstrap";
import { BsCheckLg, BsXLg } from "react-icons/bs";
import * as actions from "../../../Redux/Actions";
import {
  showErrorToastMessage,
  showSuccessToastMessage,
} from "../../../component/Toasts/Toasts";
import { icons } from "../../../assets";
import "../../SuccessionPlanning/readable-form.scss";

const SetWeightage = (props: any) => {
  const { designation, setShowModal, setSelected } = props;
  const dispatch = useDispatch();

  const [kraOptions, setKraOptions] = useState<any[]>([]);
  const [rows, setRows] = useState<any[]>([{ kraRef: "", weight: "" }]);
  const [btnDisable, setBtnDisable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addingRowIdx, setAddingRowIdx] = useState<number | null>(null);
  const [newKraName, setNewKraName] = useState("");
  const [isSubmittingKra, setIsSubmittingKra] = useState(false);

  useEffect(() => {
    // Load KRA library options
    dispatch(
      actions.getAllKrasAction((res: any) => {
        if (res?.data) {
          setKraOptions(res.data.map((k: any) => ({ value: k._id, label: k.title })));
        }
      })
    );
    // Load existing weightage for this designation
    dispatch(
      actions.getDesignationKraAction(designation._id, null, (res: any) => {
        if (res?.data && res.data.length > 0) {
          setRows(
            res.data.map((item: any) => ({
              kraRef: item.kraRef,
              weight: item.weight,
              label: item.kra?.title || "",
            }))
          );
        }
        setLoading(false);
      })
    );
  }, [designation._id]);

  const addRow = () => setRows([...rows, { kraRef: "", weight: "" }]);
  const removeRow = (idx: number) => setRows(rows.filter((_, i) => i !== idx));

  const updateRow = (idx: number, field: string, value: any) => {
    const next = [...rows];
    next[idx] = { ...next[idx], [field]: value };
    setRows(next);
  };

  // Inline: create a new KRA in the library and select it into this row.
  const handleAddNewKra = (idx: number) => {
    if (!newKraName.trim()) return;
    setIsSubmittingKra(true);
    dispatch(
      actions.createKraAction({ title: newKraName.trim() }, (res: any) => {
        setIsSubmittingKra(false);
        if (res?.error) {
          const msg =
            res?.error?.response?.data?.error?.error_description || "Failed to add KRA";
          showErrorToastMessage(msg);
        } else if (res?.data) {
          const created = res.data;
          setKraOptions((prev) => [...prev, { value: created._id, label: created.title }]);
          const next = [...rows];
          next[idx] = { ...next[idx], kraRef: created._id, label: created.title };
          setRows(next);
          setAddingRowIdx(null);
          setNewKraName("");
        }
      })
    );
  };

  const total = rows.reduce((s, r) => s + (Number(r.weight) || 0), 0);
  const allKrasChosen = rows.every((r) => r.kraRef);
  const noDuplicates = new Set(rows.map((r) => r.kraRef)).size === rows.length;
  const canSave = rows.length > 0 && allKrasChosen && noDuplicates && Math.abs(total - 100) < 0.01;

  const handleSave = () => {
    if (!canSave) return;
    setBtnDisable(true);
    const payload = {
      designationRef: designation._id,
      items: rows.map((r) => ({ kraRef: r.kraRef, weight: Number(r.weight) })),
    };
    dispatch(
      actions.setDesignationKraAction(payload, (res: any) => {
        setBtnDisable(false);
        if (res?.error) {
          const msg =
            res?.error?.response?.data?.error?.error_description ||
            "Failed to save weightage";
          showErrorToastMessage(msg);
        } else if (res?.data) {
          showSuccessToastMessage("Weightage saved successfully");
          setShowModal(false);
          setSelected(null);
        }
      })
    );
  };

  if (loading) return <p className="mt-2">Loading…</p>;

  return (
    <div className="readable-form">
      <p className="mb-3">
        Assign KRAs and their weights for <b>{designation.title}</b>. Weights must total 100%.
      </p>

      <div className="row mb-1">
        <div className="col-md-8">
          <label className="form-label mb-0">KRA</label>
        </div>
        <div className="col-md-3">
          <label className="form-label mb-0">Weight</label>
        </div>
        <div className="col-md-1"></div>
      </div>

      {rows.map((row, idx) => (
        <div className="row align-items-center mb-2" key={idx}>
          <div className="col-md-8">
            {addingRowIdx === idx ? (
              <InputGroup>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter new KRA name"
                  value={newKraName}
                  onChange={(e) => setNewKraName(e.target.value)}
                  autoFocus
                />
                <Button variant="primary" onClick={() => handleAddNewKra(idx)} disabled={isSubmittingKra}>
                  {isSubmittingKra ? <Spinner size="sm" animation="border" /> : <BsCheckLg />}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setAddingRowIdx(null);
                    setNewKraName("");
                  }}
                >
                  <BsXLg />
                </Button>
              </InputGroup>
            ) : (
              <Select
                className="dropdown-select"
                isSearchable={true}
                placeholder="Select KRA"
                value={row.kraRef ? { value: row.kraRef, label: kraOptions.find((k) => k.value === row.kraRef)?.label || row.label } : null}
                options={[
                  { value: "__ADD__", label: "+ Add New KRA" },
                  ...kraOptions.filter(
                    (opt) => !rows.some((r, i) => i !== idx && r.kraRef === opt.value)
                  ),
                ]}
                onChange={(sel: any) => {
                  if (!sel) {
                    updateRow(idx, "kraRef", "");
                    return;
                  }
                  if (sel.value === "__ADD__") {
                    setNewKraName("");
                    setAddingRowIdx(idx);
                    return;
                  }
                  updateRow(idx, "kraRef", sel.value);
                }}
                formatOptionLabel={(opt: any) =>
                  opt.value === "__ADD__" ? (
                    <span style={{ color: "#0d6efd", fontWeight: "bold" }}>{opt.label}</span>
                  ) : (
                    <span>{opt.label}</span>
                  )
                }
                styles={{
                  singleValue: (base: any) => ({ ...base, color: "#212529" }),
                  option: (base: any, state: any) => ({
                    ...base,
                    color: state.isSelected ? "#fff" : "#212529",
                  }),
                }}
              />
            )}
          </div>
          <div className="col-md-3">
            <div className="input-group">
              <input
                type="number"
                className="form-control"
                placeholder="Weight"
                min={0}
                max={100}
                value={row.weight}
                onChange={(e) => updateRow(idx, "weight", e.target.value)}
              />
              <span className="input-group-text">%</span>
            </div>
          </div>
          <div className="col-md-1">
            {rows.length > 1 && (
              <img
                title="Remove KRA"
                src={icons.delete_icon}
                alt="remove"
                className="edit-icon"
                style={{ cursor: "pointer" }}
                onClick={() => removeRow(idx)}
              />
            )}
          </div>
        </div>
      ))}

      <div className="mt-2">
        <button
          className="btn buttonsize"
          onClick={addRow}
          style={{ color: "#0d6efd", padding: 0, border: "none", background: "none", fontWeight: 500 }}
        >
          + Add KRA
        </button>
      </div>

      <div
        className="mt-3"
        style={{ fontWeight: "bold", color: Math.abs(total - 100) < 0.01 ? "#198754" : "#dc3545" }}
      >
        Total: {total}% {Math.abs(total - 100) < 0.01 ? "✓" : "(must be 100%)"}
      </div>
      {!noDuplicates && (
        <p className="error">The same KRA cannot be added more than once.</p>
      )}

      <div className="modal-btn-wrapper mt-4 mb-4">
        <div>
          <button
            className="btn cancel-btn buttonsize"
            onClick={() => {
              setShowModal(false);
              setSelected(null);
            }}
          >
            Cancel
          </button>
        </div>
        <div>
          <button
            className="btn add-submit-button buttonsize"
            disabled={!canSave || btnDisable}
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetWeightage;
